import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ExternalLink, CreditCard } from "lucide-react";

interface SubscriptionBlockModalProps {
  subscriptionStatus: string | null;
}

const statusConfig: Record<string, { title: string; description: string; badge: string; badgeVariant: "destructive" | "secondary" }> = {
  canceled: {
    title: "Subscription Cancelled",
    description: "Your subscription has been cancelled. To continue using TradeReady CRM, please reactivate your subscription.",
    badge: "Cancelled",
    badgeVariant: "destructive",
  },
  past_due: {
    title: "Payment Failed",
    description: "Your subscription payment has failed. Please update your payment method to continue using TradeReady CRM.",
    badge: "Past Due",
    badgeVariant: "destructive",
  },
  incomplete: {
    title: "Subscription Incomplete",
    description: "Your subscription setup is incomplete. Please complete your subscription to start using TradeReady CRM.",
    badge: "Incomplete",
    badgeVariant: "secondary",
  },
};

export function SubscriptionBlockModal({ subscriptionStatus }: SubscriptionBlockModalProps) {
  const status = subscriptionStatus || "canceled";
  const config = statusConfig[status] || statusConfig.canceled;

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="modal-subscription-block"
      >
        <DialogHeader className="text-center space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <div className="space-y-2">
            <DialogTitle className="text-xl flex items-center justify-center gap-2">
              {config.title}
              {/* <Badge variant={config.badgeVariant} data-testid="badge-subscription-status">
                {config.badge}
              </Badge> */}
            </DialogTitle>
            <DialogDescription className="text-base">
              {config.description}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-sm text-muted-foreground space-y-2">
            <div className="flex items-start gap-2">
              <CreditCard className="h-4 w-4 mt-0.5 text-destructive flex-shrink-0" />
              <span>
                Your account data is safe and will be available once your subscription is reactivated. No data will be lost.
              </span>
            </div>
          </div>

          <Button
            className="w-full"
            size="lg"
            onClick={() => window.open("https://tradereadycrm.com/", "_blank")}
            data-testid="button-reactivate-subscription"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Reactivate Subscription
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            You will be redirected to TradeReady CRM to manage your subscription.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
